import { useEffect, useRef, useState } from "react";
import { useIdleTimer } from "react-idle-timer";

/**
 * Calls `onStop` after `timeout` ms without user activity, counting down the last `warnFor` ms so
 * the page can warn first. Any activity during the countdown cancels it.
 *
 * @returns seconds left before the stop, or null when no countdown is running
 */
export default function useIdleStop({ timeout, warnFor, onStop }) {
  const [deadline, setDeadline] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const onStopRef = useRef(onStop);
  onStopRef.current = onStop;

  useIdleTimer({
    timeout: timeout - warnFor,
    onIdle: () => setDeadline(Date.now() + warnFor),
    onActive: () => setDeadline(null),
  });

  // Ticks against a deadline rather than decrementing, so a throttled background tab still stops on time.
  useEffect(() => {
    if (deadline === null) {
      setSecondsLeft(null);
      return undefined;
    }
    const tick = () => {
      const left = deadline - Date.now();
      if (left <= 0) {
        setDeadline(null);
        onStopRef.current?.();
      } else setSecondsLeft(Math.ceil(left / 1000));
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [deadline]);

  return secondsLeft;
}
